'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function WeightWatchersPointsCalculator() {
  const t = useTranslations("tools.weightWatchersPointsCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('latest');
  const [showResults, setShowResults] = useState(false);
  const [resultValue, setResultValue] = useState(0);
  const [resultLabel, setResultLabel] = useState('');

  // Latest Calculator (2015-2021) inputs
  const [latestInputs, setLatestInputs] = useState({
    energy: 0,
    energyUnit: '1',
    sugar: 0,
    sugarUnit: '1',
    sfat: 0,
    sfatUnit: '1',
    protein: 0,
    proteinUnit: '1',
  });

  // Old Points (2010-2015) inputs
  const [oldInputs, setOldInputs] = useState({
    protein: 0,
    proteinUnit: '1',
    carbs: 0,
    carbsUnit: '1',
    fat: 0,
    fatUnit: '1',
    fiber: 0,
    fiberUnit: '1',
  });

  // Oldest Points (Pre-2010) inputs
  const [oldestInputs, setOldestInputs] = useState({
    energy: 0,
    energyUnit: '1',
    fat: 0,
    fatUnit: '1',
    fiber: 0,
    fiberUnit: '1',
  });

  // Daily Target inputs
  const [targetInputs, setTargetInputs] = useState({
    weight: 0,
    weightUnit: 'kg',
    height: 0,
    heightUnit: 'cm',
    age: 30,
    gender: 'male',
    activity: 'sit',
  });

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1e40af',
    green: '#10b981',
    greenDark: '#059669',
    warning: '#f59e0b',
    warningBg: '#fef3c7',
    warningText: '#92400e',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto' },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    disclaimer: getResponsiveStyle(
      {
        background: colors.warningBg,
        borderLeft: `4px solid ${colors.warning}`,
        borderRadius: '0.5rem',
        color: colors.warningText,
        marginBottom: '2rem',
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    tabsContainer: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' as const },
      {},
      {},
      {}
    ),
    tabBtn: (isActive: boolean) => getResponsiveStyle(
      {
        background: isActive ? `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)` : 'white',
        color: isActive ? 'white' : colors.textPrimary,
        border: isActive ? 'none' : '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s',
        flex: '1',
        minWidth: '150px',
      },
      { padding: '0.625rem 1rem', fontSize: '0.875rem' },
      { padding: '0.75rem 1.25rem', fontSize: '0.9375rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    inputWrapper: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', alignItems: 'center' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    input: getResponsiveStyle(
      {
        flex: '1',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        transition: 'border-color 0.3s',
        width: '100%',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        background: 'white',
        cursor: 'pointer',
      },
      { padding: '0.75rem', fontSize: '1rem', width: '100%' },
      { padding: '0.875rem', fontSize: '1rem', width: 'auto' },
      { padding: '0.75rem', fontSize: '1rem', width: 'auto' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.0625rem' },
      { padding: '1rem', fontSize: '1.125rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultCard: getResponsiveStyle(
      {
        background: `linear-gradient(135deg, ${colors.green} 0%, ${colors.greenDark} 100%)`,
        color: 'white',
        borderRadius: '1rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultLabel: getResponsiveStyle(
      { opacity: 0.9, display: 'block', marginBottom: '0.5rem' },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, display: 'block' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' },
      { fontSize: '3rem' }
    ),
    resultInfo: getResponsiveStyle(
      {
        background: '#f3f4f6',
        borderRadius: '0.5rem',
        marginTop: '1rem',
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem',
      flexWrap: 'wrap' as const,
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },
  };

  const handleLatestCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const energy = parseFloat(latestInputs.energy.toString()) / parseFloat(latestInputs.energyUnit);
    const sugar = parseFloat(latestInputs.sugar.toString()) * parseFloat(latestInputs.sugarUnit);
    const sfat = parseFloat(latestInputs.sfat.toString()) * parseFloat(latestInputs.sfatUnit);
    const protein = parseFloat(latestInputs.protein.toString()) * parseFloat(latestInputs.proteinUnit);

    const points = Math.round((energy / 50) + (sugar / 10) + (sfat / 13) - (protein / 25));

    setResultLabel(t('smartPointsValue'));
    setResultValue(Math.max(0, points));
    setShowResults(true);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleOldCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const protein = parseFloat(oldInputs.protein.toString()) * parseFloat(oldInputs.proteinUnit);
    const carbs = parseFloat(oldInputs.carbs.toString()) * parseFloat(oldInputs.carbsUnit);
    const fat = parseFloat(oldInputs.fat.toString()) * parseFloat(oldInputs.fatUnit);
    const fiber = parseFloat(oldInputs.fiber.toString()) * parseFloat(oldInputs.fiberUnit);

    const calories = (protein * 4) + (carbs * 4) + (fat * 9);
    const fiberCap = Math.min(fiber, 4);

    const points = Math.round((calories / 50) + (fat / 12) - (fiberCap / 5));

    setResultLabel(t('pointsPlusValue'));
    setResultValue(Math.max(0, points));
    setShowResults(true);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleOldestCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const energy = parseFloat(oldestInputs.energy.toString()) / parseFloat(oldestInputs.energyUnit);
    const fat = parseFloat(oldestInputs.fat.toString()) * parseFloat(oldestInputs.fatUnit);
    const fiber = parseFloat(oldestInputs.fiber.toString()) * parseFloat(oldestInputs.fiberUnit);

    const fiberCap = Math.min(fiber, 4);
    const points = Math.round((energy / 50) + (fat / 12) - (fiberCap / 5));

    setResultLabel(t('pointsValue'));
    setResultValue(Math.max(0, points));
    setShowResults(true);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleTargetCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    let weight = parseFloat(targetInputs.weight.toString());
    if (targetInputs.weightUnit === 'kg') {
      weight = weight * 2.20462;
    }

    let height = parseFloat(targetInputs.height.toString());
    if (targetInputs.heightUnit === 'cm') {
      height = height / 2.54;
    } else if (targetInputs.heightUnit === 'foot') {
      height = height * 12;
    }

    const age = parseInt(targetInputs.age.toString());
    const gender = targetInputs.gender;
    const activity = targetInputs.activity;

    let points = 0;

    if (gender === 'female') {
      points += 2;
    } else {
      points += 8;
    }

    if (age >= 18 && age <= 20) {
      points += 5;
    } else if (age >= 21 && age <= 35) {
      points += 4;
    } else if (age >= 36 && age <= 50) {
      points += 3;
    } else if (age >= 51 && age <= 65) {
      points += 2;
    } else {
      points += 1;
    }

    if (height < 61) {
      points += 1;
    } else {
      points += 2;
    }

    if (weight >= 150 && weight < 175) {
      points += Math.floor((weight - 150) / 10) + 4;
    } else if (weight >= 175) {
      points += Math.floor((weight - 175) / 10) + 7;
    } else {
      points += Math.floor(weight / 10);
    }

    if (activity === 'sit') {
      points += 0;
    } else if (activity === 'stand') {
      points += 2;
    } else if (activity === 'walk') {
      points += 4;
    } else if (activity === 'hardwork') {
      points += 6;
    }

    points = Math.max(26, points);

    setResultLabel(t('dailyPointsTarget'));
    setResultValue(points);
    setShowResults(true);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.disclaimer}>
            <strong>{t('disclaimerTitle')}</strong> {t('disclaimerText')}
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={styles.tabBtn(activeTab === 'latest')}
              onClick={() => { setActiveTab('latest'); setShowResults(false); }}
            >
              {t('latestCalculatorTab')}
            </button>
            <button
              style={styles.tabBtn(activeTab === 'old')}
              onClick={() => { setActiveTab('old'); setShowResults(false); }}
            >
              {t('oldPointsTab')}
            </button>
            <button
              style={styles.tabBtn(activeTab === 'oldest')}
              onClick={() => { setActiveTab('oldest'); setShowResults(false); }}
            >
              {t('oldestPointsTab')}
            </button>
            <button
              style={styles.tabBtn(activeTab === 'target')}
              onClick={() => { setActiveTab('target'); setShowResults(false); }}
            >
              {t('dailyTargetTab')}
            </button>
          </div>

          <div style={styles.calculatorForm}>
            {/* Latest Calculator */}
            {activeTab === 'latest' && (
              <form onSubmit={handleLatestCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('foodEnergy')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={latestInputs.energy}
                      onChange={(e) => setLatestInputs({ ...latestInputs, energy: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={latestInputs.energyUnit}
                      onChange={(e) => setLatestInputs({ ...latestInputs, energyUnit: e.target.value })}
                    >
                      <option value="1">{t('calories')}</option>
                      <option value="0.2388">{t('kilojoules')}</option>
                      <option value="0.0002388">{t('joules')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('sugar')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={latestInputs.sugar}
                      onChange={(e) => setLatestInputs({ ...latestInputs, sugar: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={latestInputs.sugarUnit}
                      onChange={(e) => setLatestInputs({ ...latestInputs, sugarUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('saturatedFat')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={latestInputs.sfat}
                      onChange={(e) => setLatestInputs({ ...latestInputs, sfat: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={latestInputs.sfatUnit}
                      onChange={(e) => setLatestInputs({ ...latestInputs, sfatUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('protein')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={latestInputs.protein}
                      onChange={(e) => setLatestInputs({ ...latestInputs, protein: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={latestInputs.proteinUnit}
                      onChange={(e) => setLatestInputs({ ...latestInputs, proteinUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t('calculateButton')}
                </button>
              </form>
            )}

            {/* Old Points Calculator */}
            {activeTab === 'old' && (
              <form onSubmit={handleOldCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('protein')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldInputs.protein}
                      onChange={(e) => setOldInputs({ ...oldInputs, protein: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldInputs.proteinUnit}
                      onChange={(e) => setOldInputs({ ...oldInputs, proteinUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('carbohydrates')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldInputs.carbs}
                      onChange={(e) => setOldInputs({ ...oldInputs, carbs: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldInputs.carbsUnit}
                      onChange={(e) => setOldInputs({ ...oldInputs, carbsUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('fat')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldInputs.fat}
                      onChange={(e) => setOldInputs({ ...oldInputs, fat: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldInputs.fatUnit}
                      onChange={(e) => setOldInputs({ ...oldInputs, fatUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('fiber')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldInputs.fiber}
                      onChange={(e) => setOldInputs({ ...oldInputs, fiber: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldInputs.fiberUnit}
                      onChange={(e) => setOldInputs({ ...oldInputs, fiberUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t('calculateButton')}
                </button>
              </form>
            )}

            {/* Oldest Points Calculator */}
            {activeTab === 'oldest' && (
              <form onSubmit={handleOldestCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('foodEnergy')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldestInputs.energy}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, energy: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldestInputs.energyUnit}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, energyUnit: e.target.value })}
                    >
                      <option value="1">{t('calories')}</option>
                      <option value="4.1868">{t('kilojoules')}</option>
                      <option value="4186.8">{t('joules')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('fat')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldestInputs.fat}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, fat: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldestInputs.fatUnit}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, fatUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('fiber')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={oldestInputs.fiber}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, fiber: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={oldestInputs.fiberUnit}
                      onChange={(e) => setOldestInputs({ ...oldestInputs, fiberUnit: e.target.value })}
                    >
                      <option value="1">{t('grams')}</option>
                      <option value="1000">{t('kilograms')}</option>
                      <option value="28.3495">{t('ounce')}</option>
                      <option value="453.592">{t('pound')}</option>
                    </select>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t('calculateButton')}
                </button>
              </form>
            )}

            {/* Daily Target Calculator */}
            {activeTab === 'target' && (
              <form onSubmit={handleTargetCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('bodyWeight')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={targetInputs.weight}
                      onChange={(e) => setTargetInputs({ ...targetInputs, weight: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={targetInputs.weightUnit}
                      onChange={(e) => setTargetInputs({ ...targetInputs, weightUnit: e.target.value })}
                    >
                      <option value="kg">{t('kilograms')}</option>
                      <option value="pound">{t('pounds')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('bodyHeight')}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      style={styles.input}
                      value={targetInputs.height}
                      onChange={(e) => setTargetInputs({ ...targetInputs, height: parseFloat(e.target.value) || 0 })}
                    />
                    <select
                      style={styles.select}
                      value={targetInputs.heightUnit}
                      onChange={(e) => setTargetInputs({ ...targetInputs, heightUnit: e.target.value })}
                    >
                      <option value="cm">{t('centimeters')}</option>
                      <option value="inch">{t('inches')}</option>
                      <option value="foot">{t('feet')}</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('age')}</label>
                  <input
                    type="number"
                    min="18"
                    max="120"
                    required
                    style={{ ...styles.input, width: '100%' }}
                    value={targetInputs.age}
                    onChange={(e) => setTargetInputs({ ...targetInputs, age: parseInt(e.target.value) || 18 })}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('gender')}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={targetInputs.gender === 'male'}
                        onChange={(e) => setTargetInputs({ ...targetInputs, gender: e.target.value })}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <span>{t('male')}</span>
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={targetInputs.gender === 'female'}
                        onChange={(e) => setTargetInputs({ ...targetInputs, gender: e.target.value })}
                        style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                      />
                      <span>{t('female')}</span>
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('activityLevel')}</label>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={targetInputs.activity}
                    onChange={(e) => setTargetInputs({ ...targetInputs, activity: e.target.value })}
                  >
                    <option value="sit">{t('activitySit')}</option>
                    <option value="stand">{t('activityStand')}</option>
                    <option value="walk">{t('activityWalk')}</option>
                    <option value="hardwork">{t('activityHardwork')}</option>
                  </select>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t('calculateButton')}
                </button>
              </form>
            )}
          </div>

          {/* Results */}
          {showResults && (
            <div style={styles.results} id="results">
              <h3 style={{ ...styles.h2, textAlign: 'center' }}>{t('yourResults')}</h3>
              <div style={styles.resultCard}>
                <span style={styles.resultLabel}>{resultLabel}</span>
                <span style={styles.resultValue}>{resultValue}</span>
              </div>
              <div style={styles.resultInfo}>
                <h4 style={{ ...styles.label, marginBottom: '1rem', fontSize: '1.1rem' }}>
                  💡 {t('tipsForSuccess')}
                </h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ padding: '0.5rem 0', color: colors.textSecondary, borderBottom: '1px solid #e5e7eb' }}>
                    {t('tip1')}
                  </li>
                  <li style={{ padding: '0.5rem 0', color: colors.textSecondary, borderBottom: '1px solid #e5e7eb' }}>
                    {t('tip2')}
                  </li>
                  <li style={{ padding: '0.5rem 0', color: colors.textSecondary, borderBottom: '1px solid #e5e7eb' }}>
                    {t('tip3')}
                  </li>
                  <li style={{ padding: '0.5rem 0', color: colors.textSecondary, borderBottom: '1px solid #e5e7eb' }}>
                    {t('tip4')}
                  </li>
                  <li style={{ padding: '0.5rem 0', color: colors.textSecondary }}>
                    {t('tip5')}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li>{t('howToStep1')}</li>
          <li>{t('howToStep2')}</li>
          <li>{t('howToStep3')}</li>
          <li>{t('howToStep4')}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <ul style={styles.list}>
          <li>{t('insight1')}</li>
          <li>{t('insight2')}</li>
          <li>{t('insight3')}</li>
          <li>{t('insight4')}</li>
          <li>{t('insight5')}</li>
        </ul>
      </section>

      {/* Understanding the Formulas */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('formulasTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            {t('latestFormulaTitle')}
          </h3>
          <p style={styles.paragraph}>{t('latestFormulaDesc')}</p>
          <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', fontFamily: 'monospace' }}>
            {t('latestFormulaCode')}
          </div>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            {t('oldFormulaTitle')}
          </h3>
          <p style={styles.paragraph}>{t('oldFormulaDesc')}</p>
        </div>
        <div>
          <h3 style={{ ...styles.label, fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            {t('oldestFormulaTitle')}
          </h3>
          <p style={styles.paragraph}>{t('oldestFormulaDesc')}</p>
        </div>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('faq1Question')}
          </h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('faq2Question')}
          </h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('faq3Question')}
          </h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.label, fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('faq4Question')}
          </h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{ ...styles.label, fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('faq5Question')}
          </h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={{ ...styles.list, listStyle: 'decimal' }}>
          <li>
            <a
              href="https://www.youtube.com/watch?v=EXQ670KjIeQ"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref1')}
            </a>
          </li>
          <li>
            <a
              href="https://www.cleaneatzkitchen.com/a/blog/how-to-calculate-weight-watchers-smart-points-2023"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref2')}
            </a>
          </li>
          <li>
            <a
              href="https://www.calculator.net/weight-watchers-points-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref3')}
            </a>
          </li>
          <li>
            <a
              href="https://emilybites.com/2024/12/new-ww-points-program-changes-2024-2025.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref4')}
            </a>
          </li>
          <li>
            <a
              href="https://www.watcherspoint.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref5')}
            </a>
          </li>
          <li>
            <a
              href="https://www.weightwatchers.com/us/how-it-works/points-program"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              {t('ref6')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}